/*
 * Frido Mini-PDP popup controller — plain IIFE, no framework, no web component.
 *
 * Drives the closed top-layer <dialog class="frido-mini-pdp__dialog"> authored
 * by frido-mini-pdp.liquid. The dialog HOSTS real picker instances (spliced into
 * its picker container by a separate move-slot script); this controller never
 * builds option pills. Its job is the surrounding chrome — open/close, scroll
 * lock, header repricing on variant change, add-to-cart, and hoisting the page's
 * size-guide overlay above the top-layer dialog.
 *
 * Mirrors the fsvfrd01 quickshop sheet behaviour 1:1:
 *   - open: showModal() + document.body.style.overflow='hidden'
 *   - ATC: window.Spectrum.cart.addAndOpen({ id, quantity }, { sourceId })
 *   - size-guide: move .sai-fsgfrd01__root INTO the dialog (a <body> overlay
 *     sits UNDER a top-layer <dialog>), raise it, restore to <body> on close.
 */
;(() => {
  // Module singleton guard — the snippet may be present more than once in a
  // rendered page (draft + live wrappers, repeated sections); only the first
  // load wires listeners.
  if (window.__frido_mini_pdp_init__) return
  window.__frido_mini_pdp_init__ = true

  const SOURCE_ID = 'frido-mini-pdp'
  const OPEN_CLASS = 'frido-mini-pdp__dialog--open'

  // Strip currency symbols / grouping so a money-formatted string reduces to a
  // number for the discount computation. Holds for ₹ and $ alike.
  function parseMoney(str) {
    if (typeof str !== 'string') return Number(str) || 0
    const cleaned = str.replace(/[^0-9.]/g, '')
    return cleaned ? Number.parseFloat(cleaned) : 0
  }

  function readVariantPool(dialog) {
    // The pool <script> is a SIBLING of the <dialog> (both inlined in the
    // section), not a descendant — search near the dialog / the document,
    // never dialog.querySelector (which only finds descendants → empty pool →
    // the header never reprices on variant change).
    const el =
      (dialog.parentElement && dialog.parentElement.querySelector('[data-mini-pdp-variants]')) ||
      document.querySelector('[data-mini-pdp-variants]')
    if (!el) return []
    try {
      const parsed = JSON.parse(el.textContent || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  class MiniPdp {
    constructor(dialog) {
      this.dialog = dialog
      this.panel = dialog.querySelector('.frido-mini-pdp__panel')
      this.pool = readVariantPool(dialog)
      // The hoisted size-guide root is returned to <body> on close; track it so
      // an interrupted session (Esc, backdrop) still restores it.
      this._hoistedSizeGuide = null
      this._openTimer = null

      this._bind()
    }

    _bind() {
      // Open on request from the trigger (a card CTA, a "customise" button…).
      // Optional `event.detail.variant` lets the opener seed the header for the
      // variant the user tapped.
      window.addEventListener('spectrum:mini-pdp:open', (e) => {
        this.open(e?.detail?.variant)
      })

      // Reprice when a hosted picker announces a variant change. The pickers
      // write the RESOLVED variant (across all axes) into the cart form input;
      // the event's own detail is often empty, so read the form after a frame
      // (the picker writes the input around the same tick it dispatches).
      window.addEventListener('spectrum:variant-change', (e) => {
        requestAnimationFrame(() => this.reprice(e?.detail?.variant))
      })

      // Close paths: the close button, a backdrop click (target is the dialog
      // element itself — it fills the viewport but paints nothing, so the area
      // outside the panel is the dialog), and Esc (native `cancel`).
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) {
          this.close()
          return
        }
        if (e.target.closest?.('[data-mini-pdp-close]')) {
          this.close()
        }
      })

      this.dialog.addEventListener('cancel', (e) => {
        // Take over Esc so the slide-down animation + size-guide restore run;
        // the default would tear the dialog down instantly.
        e.preventDefault()
        this.close()
      })

      // ATC + size-guide are delegated so they survive picker re-renders.
      this.dialog.addEventListener('click', (e) => {
        if (e.target.closest?.('[data-mini-pdp-atc]')) {
          this._onAtc(e.target.closest('[data-mini-pdp-atc]'))
        } else if (e.target.closest?.('[data-mini-pdp-sizeguide]')) {
          this._openSizeGuide()
        }
      })
    }

    _titleCaseLabels() {
      // Live modal shows "Size:", "Cover Options:" — the pickers emit uppercase
      // label text, so rewrite text here (popup only; buybox untouched).
      this.dialog.querySelectorAll('[class*="__label-key"]').forEach((el) => {
        const t = el.textContent || ''
        const fixed = t.toLowerCase().replace(/(^|[\s(])([a-z])/g, (m, p, c) => p + c.toUpperCase()).replace(/\s+:/g, ':')
        if (fixed !== t) el.textContent = fixed
      })
    }

    open(variant) {
      if (this.dialog.open) return
      this.dialog.showModal()
      document.body.style.overflow = 'hidden'
      if (variant) this.reprice(variant)
      // Re-evaluate the size-guide row against the live page: the row is
      // SSR-shown only when the product has a size axis, but the button must
      // never be a dead no-op, so also require a page size-guide snippet.
      const sizeGuide = this.dialog.querySelector('[data-mini-pdp-sizeguide]')
      if (sizeGuide) {
        if (!sizeGuide.hidden) sizeGuide.hidden = !document.querySelector('sai-fsgfrd01')
        // Match the PDP: the Size Guide sits directly under the Size picker, not
        // at the bottom of the sheet. Pickers are hosted (moved in), so their
        // order is only known at runtime — reposition the row here on open.
        const sizePicker = this.dialog.querySelector('[data-spectrum-snippet-id="fszfrd01"]')
        if (sizePicker) {
          const host = sizePicker.closest('[data-spectrum-lq-snippet]') || sizePicker
          if (host.lastElementChild !== sizeGuide) host.appendChild(sizeGuide)
        }
      }
      this._titleCaseLabels()
      if (!this._labelObserver) {
        const host = this.dialog.querySelector('[data-mini-pdp-pickers]')
        if (host) {
          this._labelObserver = new MutationObserver(() => this._titleCaseLabels())
          this._labelObserver.observe(host, { childList: true, subtree: true, characterData: true })
        }
      }
      // Reflow, then flip `--open` so the panel's transform transition runs (a
      // transition off display:none never fires) — same trick as fsvfrd01.
      void this.dialog.offsetHeight
      this.dialog.classList.add(OPEN_CLASS)
    }

    close() {
      if (!this.dialog.open) return
      // Start the slide with ZERO other work in this frame — any synchronous
      // DOM mutation here (size-guide move, ATC reset, observer churn) forces
      // layout at animation start and janks the first frames. All housekeeping
      // runs in finalize, after the 0.5s slide-down completes.
      this.dialog.classList.remove(OPEN_CLASS)
      // Finalize on the wrap's ACTUAL transitionend, not a guessed timer: the
      // transition starts a frame after the class flips, so a 500ms timer fires
      // before the last frames paint and dialog.close() jump-cuts the slide at
      // the bottom. The timeout is only a safety net if the event never fires.
      const wrap = this.dialog.querySelector('.frido-mini-pdp__wrap')
      let finalized = false
      const finalize = () => {
        if (finalized) return
        finalized = true
        if (this._openTimer) clearTimeout(this._openTimer)
        this._restoreSizeGuide()
        this._resetAtc()
        if (this.dialog.open) this.dialog.close()
        document.body.style.overflow = ''
      }
      if (wrap) {
        wrap.addEventListener(
          'transitionend',
          (e) => {
            if (e.target === wrap && e.propertyName === 'transform') finalize()
          },
          { once: true }
        )
      }
      if (this._openTimer) clearTimeout(this._openTimer)
      this._openTimer = setTimeout(finalize, 700)
    }

    // Re-enable + relabel the ATC button so it never sticks on "Adding…" — run
    // on every close (including after a successful add) and as the no-SDK
    // fallback. `dataset.label` is the SSR baseline ("Add to cart"/"Sold out").
    _resetAtc() {
      const atc = this.dialog.querySelector('[data-mini-pdp-atc]')
      if (atc) {
        atc.disabled = false
        atc.textContent = atc.dataset.label || 'Add To Cart'
      }
    }

    // Paint the header for a variant. Prefers the money-formatted pool entry
    // (looked up by id) so no money formatting happens here; falls back to the
    // event's own fields when the variant isn't in the pool.
    reprice(variant) {
      // Resolve the authoritative variant id: prefer the event's own id, else
      // the cart form's current selection (the pickers write it there across
      // all axes). Without this the header never follows a variant change,
      // because the picker's event detail is empty.
      let id = variant && variant.id != null ? String(variant.id) : ''
      if (!id) {
        const input = document.querySelector('form[action*="/cart/add"] input[name="id"]')
        id = input?.value?.trim() || ''
      }
      const pooled = this.pool.find((v) => String(v.id) === id)
      if (!pooled && !variant) return

      const price = pooled?.price ?? variant?.price ?? ''
      const compareAt = pooled?.compare_at ?? variant?.compare_at_price ?? variant?.compare_at ?? null
      const imgElForDefault = this.dialog.querySelector('[data-mini-pdp-image]')
      const defaultImg = imgElForDefault?.dataset.miniPdpDefaultImg || ''
      // Variant image if it has one, else the product's main image — never keep
      // a stale previous image (some variants, e.g. Cooling Cover, have none).
      const image = pooled?.image || variant?.featured_image || variant?.image || defaultImg
      const available =
        pooled?.available ?? (typeof variant?.available === 'boolean' ? variant.available : true)

      const priceEl = this.dialog.querySelector('[data-mini-pdp-price]')
      if (priceEl && price) priceEl.textContent = price

      const compareEl = this.dialog.querySelector('[data-mini-pdp-compare]')
      if (compareEl) {
        if (compareAt) {
          compareEl.textContent = compareAt
          compareEl.hidden = false
        } else {
          compareEl.textContent = ''
          compareEl.hidden = true
        }
      }

      const discountEl = this.dialog.querySelector('[data-mini-pdp-discount]')
      if (discountEl) {
        const now = parseMoney(price)
        const was = parseMoney(compareAt)
        const pct = was > 0 && now > 0 && was > now ? Math.round(((was - now) / was) * 100) : 0
        if (pct > 0) {
          discountEl.textContent = `${pct}% OFF`
          discountEl.hidden = false
        } else {
          discountEl.textContent = ''
          discountEl.hidden = true
        }
      }

      const imgEl = this.dialog.querySelector('[data-mini-pdp-image]')
      if (imgEl && image) imgEl.src = image

      const atc = this.dialog.querySelector('[data-mini-pdp-atc]')
      if (atc) {
        atc.disabled = !available
        atc.textContent = available ? atc.dataset.label || 'Add To Cart' : 'Sold Out'
      }
    }

    // Resolve the currently selected variant id. The hosted pickers write the
    // selection into the section's /cart/add form input, so that is the source
    // of truth; the pool's first available entry is the fallback.
    _resolveVariantId() {
      const input = document.querySelector('form[action*="/cart/add"] input[name="id"]')
      const fromForm = input?.value?.trim()
      if (fromForm) return fromForm
      const firstAvailable = this.pool.find((v) => v.available)
      return firstAvailable ? String(firstAvailable.id) : ''
    }

    _onAtc(btn) {
      const variantId = this._resolveVariantId()
      const pooled = this.pool.find((v) => String(v.id) === String(variantId))
      // Availability gate — disable + label "Sold out" for an unavailable pick.
      if (!variantId || (pooled && pooled.available === false)) {
        btn.disabled = true
        btn.textContent = 'Sold out'
        return
      }

      btn.disabled = true
      btn.textContent = 'Adding…'

      const sdk = window.Spectrum?.cart
      if (sdk && typeof sdk.addAndOpen === 'function') {
        // Add, then ALWAYS close the mini-PDP — close() re-enables + relabels the
        // ATC, so it can never stick on "Adding…", regardless of whether
        // addAndOpen returns a promise, resolves, or rejects. Closing also lifts
        // this top-layer <dialog> off the cart drawer (a <body> overlay) so the
        // drawer is visible. variantId passed verbatim (the SDK normalises it).
        Promise.resolve()
          .then(() => sdk.addAndOpen({ id: variantId, quantity: 1 }, { sourceId: SOURCE_ID }))
          .catch(() => {})
          .finally(() => this.close())
      } else {
        this._resetAtc()
      }
    }

    // Open the page's size-guide snippet (sai-fsgfrd01) OVER this dialog without
    // closing it. fsgfrd01 renders a position:fixed overlay portaled to <body>;
    // a <body> overlay sits UNDER a top-layer showModal <dialog>, so move that
    // overlay INTO this dialog (same top layer) and raise it. Its own
    // close/backdrop handlers stay bound to the root, so closing it returns
    // here. No-ops when no size guide is on the page. NEVER navigates.
    _openSizeGuide() {
      const root = document.querySelector('.sai-fsgfrd01__root')
      if (!root) {
        // Not rendered yet — fall back to fsgfrd01's own standalone open.
        window.dispatchEvent(new CustomEvent('spectrum:size-guide-open'))
        return
      }
      // Remember the original parent so close() can put it back under <body>.
      this._hoistedSizeGuide = { node: root, parent: root.parentNode, next: root.nextSibling }
      this.dialog.appendChild(root)
      root.style.zIndex = '40'
      root.dataset.open = 'true'
      document.body.style.overflow = 'hidden'
    }

    _restoreSizeGuide() {
      // Collapse a size guide we hoisted into the dialog and return it to its
      // original <body> position so it doesn't re-show on the next open.
      const sgRoot = this.dialog.querySelector('.sai-fsgfrd01__root')
      if (sgRoot) {
        sgRoot.dataset.open = 'false'
        sgRoot.style.zIndex = ''
      }
      const hoisted = this._hoistedSizeGuide
      if (hoisted?.node && hoisted.parent) {
        if (hoisted.next && hoisted.next.parentNode === hoisted.parent) {
          hoisted.parent.insertBefore(hoisted.node, hoisted.next)
        } else {
          hoisted.parent.appendChild(hoisted.node)
        }
      }
      this._hoistedSizeGuide = null
    }
  }

  function init() {
    const dialog = document.querySelector('.frido-mini-pdp__dialog[data-mini-pdp]')
    if (!dialog) return
    const controller = new MiniPdp(dialog)
    // Seed the ATC label baseline so repricing back to available restores it.
    const atc = dialog.querySelector('[data-mini-pdp-atc]')
    if (atc && !atc.disabled) atc.dataset.label = atc.textContent
    else if (atc) atc.dataset.label = 'Add To Cart'
    window.__frido_mini_pdp__ = controller
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
